import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import type { CorrectiveAction, Reflection } from "../types";

export const useCorrectiveActions = (userId: string | undefined) => {
	const [correctiveActions, setCorrectiveActions] = useState<
		CorrectiveAction[]
	>([]);
	const [activeReflections, setActiveReflections] = useState<Reflection[]>([]);
	const [loading, setLoading] = useState(true);

	const fetchActiveReflections = useCallback(async () => {
		if (!userId) return;

		try {
			const { data, error } = await supabase
				.from("reflections")
				.select("id, title, description, is_draft, archived")
				.eq("user_id", userId)
				.eq("is_draft", false)
				.eq("archived", false)
				.order("created_at", { ascending: false });

			if (error) {
				console.error("Supabase error fetching active reflections:", error);
				throw error;
			}
			setActiveReflections(data || []);
		} catch (error) {
			console.error("Error fetching active reflections:", error);
		}
	}, [userId]);

	const fetchCorrectiveActions = useCallback(async () => {
		if (!userId) return;

		try {
			const { data: actionsData, error: actionsError } = await supabase
				.from("corrective_actions")
				.select(
					`
          *,
          reflections!corrective_actions_reflection_id_fkey(
            id,
            title,
            description
          )
        `,
				)
				.eq("reflections.user_id", userId)
				.order("priority", { ascending: true });

			if (actionsError) {
				console.error(
					"Supabase error fetching corrective actions:",
					actionsError,
				);
				throw actionsError;
			}

			if (actionsData && actionsData.length > 0) {
				const actionsWithDetails = await Promise.all(
					actionsData.map(async (action) => {
						const reflectionId = action.reflection_id;

						const { data: signalsData } = await supabase
							.from("signals")
							.select("id, title, description")
							.eq("reflection_id", reflectionId);

						const { data: factorsData } = await supabase
							.from("contributing_factors")
							.select("id, title, description")
							.eq("reflection_id", reflectionId);

						return {
							...action,
							reflection: action.reflections,
							signals: signalsData || [],
							contributing_factors: factorsData || [],
						};
					}),
				);

				setCorrectiveActions(actionsWithDetails);
			}
		} catch (error) {
			const errorMessage =
				error instanceof Error
					? error.message
					: "An unexpected error occurred while fetching corrective actions";
			console.error("Error fetching corrective actions:", errorMessage, error);
		} finally {
			setLoading(false);
		}
	}, [userId]);

	useEffect(() => {
		if (userId) {
			fetchCorrectiveActions();
			fetchActiveReflections();
		}
	}, [userId, fetchCorrectiveActions, fetchActiveReflections]);

	const refetch = useCallback(() => {
		fetchCorrectiveActions();
		fetchActiveReflections();
	}, [fetchCorrectiveActions, fetchActiveReflections]);

	return {
		correctiveActions,
		activeReflections,
		loading,
		refetch,
		setCorrectiveActions,
	};
};
